import React, { createContext, useContext, useState, useEffect } from 'react';
import { en, TranslationType } from '../locales/en';
import { es } from '../locales/es';
import { pt } from '../locales/pt';

export type LanguageCode = 'en' | 'es' | 'pt';

interface LanguageContextType {
  language: LanguageCode;
  translations: TranslationType;
  setLanguage: (lang: LanguageCode) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations: Record<LanguageCode, TranslationType> = {
  en,
  es,
  pt,
};

const STORAGE_KEY = 'dominion-risk-profile-language';

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>(() => {
    // Try to get language from localStorage, default to 'en'
    const stored = localStorage.getItem(STORAGE_KEY);
    return (stored as LanguageCode) || 'en';
  });

  const setLanguage = (lang: LanguageCode) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  };

  useEffect(() => {
    // Set HTML lang attribute for accessibility
    document.documentElement.lang = language;
    // Update page title based on language
    document.title = translations[language].pageTitle;
  }, [language]);

  const value: LanguageContextType = {
    language,
    translations: translations[language],
    setLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}